/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getDisplayPrice} from '@salesforce/retail-react-app/app/utils/product-utils'
import _ from 'lodash'
import {useEffect, useState, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'

import {useVariant} from './use-variant'
import {useVariantProductDetails} from './use-variant-product-details'
import {
    buildVariantValueHref,
    useVariationAttributes
} from './use-variation-attributes'
import {useVariationParams} from './use-variation-params'
import {usePDPSearchParams} from '@salesforce/retail-react-app/app/hooks/use-pdp-search-params'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

const OUT_OF_STOCK = 'OUT_OF_STOCK'
const UNFULFILLABLE = 'UNFULFILLABLE'
const LIMITED_STOCK = 'LIMITED_STOCK'

// TODO: This needs to be refactored.
export const useDerivedProduct = (product, isProductPartOfSet = false) => {
    const {data: currentBasket} = useCurrentBasket()
    const history = useHistory()
    const existingParams = usePDPSearchParams(product?.id)
    const intl = useIntl()
    const variant = useVariant(product, isProductPartOfSet)
    const {product: derivedProductDetails} = useVariantProductDetails()

    const derivedProduct = useMemo(() => {
        if (product?.id === variant?.productId) {
            return product
        }

        return {
            ...product,
            ...derivedProductDetails
        }
    }, [product, derivedProductDetails])

    const showLoading = useMemo(
        () => !product || !derivedProduct,
        [product, derivedProduct]
    )
    const ats = useMemo(() => {
        return derivedProduct?.inventory?.ats || 0
    }, [derivedProduct, variant])

    const preorderable = useMemo(
        () => derivedProduct?.inventory?.preorderable || false,
        [derivedProduct]
    )
    const stepQuantity = product?.stepQuantity || 1
    const minOrderQuantity = ats > 0 ? product?.minOrderQuantity || 1 : 0
    const maxOrderQuantity = 3
    const initialQuantity = product?.quantity || product?.minOrderQuantity || 1
    const c_valueSet = product?.c_valueSet

    const variationParams = useVariationParams(product, isProductPartOfSet)
    const variationAttributes = useVariationAttributes(
        product,
        isProductPartOfSet
    )

    useEffect(() => {
        if (variationAttributes.length) {
            const isEmptySelection = _.some(
                variationAttributes,
                (attribute) => !_.get(variationParams, attribute.id, null)
            )

            if (isEmptySelection) {
                const keys = _.map(
                    variationAttributes,
                    (attribute) => attribute.id
                )
                const variantsForCurrentSelection = _.filter(
                    product.variants,
                    (variant) =>
                        _.isMatch(
                            variant.variationValues,
                            _.pick(variationParams, keys)
                        ) && variant.orderable
                )

                const currentVariant = _.head(variantsForCurrentSelection)
                if (currentVariant) {
                    const params = {
                        ...variationParams,
                        ...(currentVariant?.variationValues || {}),
                        pid: currentVariant?.productId
                    }
                    const href = buildVariantValueHref({
                        pathname: location.pathname,
                        existingParams: existingParams,
                        newParams: params,
                        productId: product.id,
                        isProductPartOfSet
                    })

                    history.replace(href)
                }
            }
        }
    }, [variationAttributes, variationParams, product, existingParams])
    const [quantity, setQuantity] = useState(initialQuantity)

    // A product is considered out of stock if the stock level is 0 or if we have all our
    // variation attributes selected, but don't have a variant. We do this because the API
    // will sometimes return all the variants even if they are out of stock, but for other
    // products it won't.
    const variantInCart = currentBasket?.productItems?.find(
        (product) => product?.productId === variant?.productId
    )

    const isOutOfStock =
        variant?.orderable === true && !ats
            ? false
            : !(ats - (variantInCart?.quantity || 0)) ||
              (!variant &&
                  !!variationParams &&
                  !!Object.keys(variationParams)?.length &&
                  !!variationAttributes?.length &&
                  Object.keys(variationParams).length ===
                      variationAttributes.length)

    const unfulfillable = ats < quantity
    const limitedInventory = ats <= 3

    const inventoryMessages = {
        [OUT_OF_STOCK]: intl.formatMessage({
            defaultMessage: 'Out of stock',
            id: 'use_product.message.out_of_stock'
        }),
        [UNFULFILLABLE]: intl.formatMessage(
            {
                defaultMessage: 'Only {ats} remaining!',
                id: 'use_product.message.inventory_remaining'
            },
            {ats}
        ),
        [LIMITED_STOCK]: `Only ${ats} left!`
    }
    const showInventoryMessage =
        variant && variant?.orderable === true
            ? false
            : variant && (isOutOfStock || unfulfillable)
    const inventoryMessage =
        (isOutOfStock && inventoryMessages[OUT_OF_STOCK]) ||
        (unfulfillable && inventoryMessages[UNFULFILLABLE]) ||
        (limitedInventory && inventoryMessages[LIMITED_STOCK])

    const {basePrice, discountPrice} = getDisplayPrice(product)

    // If the `initialQuantity` changes, update the state. This typically happens
    // when either the master product changes, or the inventory of the product changes
    // from out-of-stock to in-stock or vice versa.
    useEffect(() => {
        setQuantity(initialQuantity)
    }, [initialQuantity])

    return {
        showLoading,
        showInventoryMessage,
        inventoryMessage,
        variationAttributes,
        quantity,
        minOrderQuantity,
        maxOrderQuantity,
        stepQuantity,
        variationParams,
        setQuantity,
        variant,
        ats,
        basePrice,
        discountPrice,
        isOutOfStock,
        preorderable,
        c_valueSet
    }
}
